import { useState, useEffect, useRef, useCallback } from "react";
import apiClient from "../../../shared/API/apiClient";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { getDepartmentDistribution } from "../../../services/departmentDistributionService";
import { useReportingYear } from "../../../context/ReportingYearContext";

// COLORS
const COLORS = ["#2e944b", "#f59e0b", "#3b82f6"];

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="4"
      width="18"
      height="18"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
    <line
      x1="3"
      y1="10"
      x2="21"
      y2="10"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const ByDepartment = () => {
  const {
    reportingYear,
    setReportingYear,
    reportingYearId,
  } = useReportingYear();

  const [legalEntities, setLegalEntities] = useState([]);
  

  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
   const [selectedEntity, setSelectedEntity] = useState("");

  // const [entityLoading, setEntityLoading] = useState(true);

  const formatISO = (d) => d.toISOString().split("T")[0];

  const parseFinancialYear = (fy) => {
    if (!fy) return {};

    const parts = fy.split("-");

    const startYear = Number(parts[0]);

    let endYear;

    if (parts[1]?.length === 2) {
      endYear = Number(`20${parts[1]}`);
    } else {
      endYear = Number(parts[1]);
    }

    return { startYear, endYear };
  };

  const getDefaultDates = (fy) => {
    const { startYear, endYear } = parseFinancialYear(fy);

    return {
      from: `${startYear}-04-01`,
      to: `${endYear}-03-31`,
    };
  };

  const getFinancialYearDates = (fy) => {
    const { startYear, endYear } = parseFinancialYear(fy);

    return {
      minDate: `${startYear}-04-01`,
      maxDate: `${endYear}-03-31`,
    };
  };

  const getFYFromDate = (dateStr) => {
    const date = new Date(dateStr);

    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    let startYear;
    let endYear;

    if (month >= 4) {
      startYear = year;
      endYear = year + 1;
    } else {
      startYear = year - 1;
      endYear = year;
    }

    return `${startYear}-${String(endYear).slice(2)}`;
  };

  const formatDisplay = (d) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeRange, setActiveRange] = useState(null);

  const { minDate, maxDate } = getFinancialYearDates(reportingYear);

  const [compareFrom, setCompareFrom] = useState("");
  const [compareTo, setCompareTo] = useState("");

  const fromRef = useRef();
  const toRef = useRef();
  const compareFromRef = useRef();
  const compareToRef = useRef();

  const ranges = ["This Month", "Last Month", "This Quarter", "This Year"];

  const filterMap = {
    "This Month": "THIS_MONTH",
    "Last Month": "LAST_MONTH",
    "This Quarter": "THIS_QUARTER",
    "This Year": "THIS_YEAR",
  };

  const [appliedFilter, setAppliedFilter] = useState({
    filter: null,
    fromDate: null,
    toDate: null,
  });

  const openPicker = (ref) => {
    if (!ref.current) return;
    if (ref.current.showPicker) ref.current.showPicker();
    else ref.current.click();
  };

  //API Legal Entities

  useEffect(() => {
    const fetchLegalEntities = async () => {
      if (!reportingYearId) return;

      setLoading(true);

      try {
        const res = await apiClient.get(`/api/legal-entities-by-year`, {
          params: {
            reporting_year_id: reportingYearId,
          },
        });

        if (res.data.code === 200) {
          const entities = res.data.data || [];

          setLegalEntities(entities);

       
        } else {
          setLegalEntities([]);
        }
      } catch (err) {
        console.error("Legal entities API error:", err);
        setLegalEntities([]);
      } finally {
        setLoading(true);
      }
    };

    fetchLegalEntities();
  }, [reportingYearId]);

  useEffect(() => {
    if (!reportingYear) return;

    const defaults = getDefaultDates(reportingYear);

    setFromDate(defaults.from);
    setToDate(defaults.to);

    setActiveRange(null);

    setAppliedFilter({
      filter: null,
      fromDate: defaults.from,
      toDate: defaults.to,
    });
  }, [reportingYear]);

  // API CALL
  const fetchDepartmentDistribution = useCallback(async () => {
    if (!reportingYearId || !selectedEntity) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const res = await getDepartmentDistribution(
        reportingYear,
        appliedFilter.filter ?? "",
        appliedFilter.fromDate ?? "",
        appliedFilter.toDate ?? "",
        selectedEntity,
        reportingYearId
      );

      if (res.data.success) {
        setApiData(res.data);
      } else {
        setApiData(null);
      }
    } catch (err) {
      console.error("Department API Error:", err);
      setApiData(null);
    } finally {
      setLoading(false);
    }
  }, [reportingYearId, selectedEntity, reportingYear, appliedFilter]);

  useEffect(() => {
    fetchDepartmentDistribution();
  }, [fetchDepartmentDistribution]);

 
 

    {loading && (
      <div className="dash-loader-wrapper">
        <div className="dash-loader"></div>
        <p className="dash-loader-text">Loading ByDepartment...</p>
      </div>
      )}

  // DATA MAPPING

  const departments = apiData?.departments || [];

  const tableData = departments.map((item) => ({
    location: item.department_name,
    scope1: Number(item.scope1_emissions),
    scope2: Number(item.scope2_emissions),
    total: Number(item.total_emissions),
    percent: item.percent_of_total,
  }));

  const pieData = departments.map((item) => ({
    name: item.department_name,
    value: Number(item.total_emissions),
  }));

  const barData = departments.map((item) => ({
    name: item.department_name,
    scope1: Number(item.scope1_emissions),
    scope2: Number(item.scope2_emissions),
  }));

  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    percent,
    name,
    value,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25; // line length
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#333"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
      >
        {name} ({value})
      </text>
    );
  };

  const isMobile = window.innerWidth < 768;

  <Pie
    data={pieData}
    dataKey="value"
    cx="50%"
    cy="50%"
    outerRadius={isMobile ? 70 : 100}
    innerRadius={isMobile ? 35 : 48}
    labelLine={!isMobile} // mobile pe label line hata do
    label={isMobile ? false : renderCustomLabel}
  />;

  //No data component

  const NoData = ({ message }) => (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        color: "#999",
        fontSize: 13,
        textAlign: "center",
        width: "100%",
        pointerEvents: "none",
      }}
    >
      {message}
    </div>
  );

  return (
    <div className="dash-page">
      <div className="dash-page-title">Department-wise Distribution</div>
      <div className="dash-page-subtitle">
        Internal report — not part of SEBI BRSR filing
      </div>

      <div className="dash-tag">Internal Use Only</div>

      <div className="filter-card">
        <div className="filter-group">
          <label>Reporting Year *</label>

          <input
            className="filter-input"
            value={reportingYear || ""}
            disabled
          />
        </div>

        <div className="filter-group">
          <label>Legal Entity *</label>

          <select
            className="filter-input"
            value={selectedEntity}
            onChange={(e) => {
              setSelectedEntity(e.target.value);
              setLoading(true);
            }}
          >
            <option value="">Select</option>

            {legalEntities.map((entity) => (
              <option key={entity.entity_id} value={entity.entity_id}>
                {entity.entity_name}
              </option>
            ))}
          </select>
        </div>
      </div>

       {/* EMPTY STATE */}
      {!selectedEntity && (
        <div className="st-empty-entity">
          <div className="st-empty-entity__icon">
            <i className="fa-solid fa-building" />
          </div>

          <div className="st-empty-entity__title">
            Select a Legal Entity to continue
          </div>

          <div className="st-empty-entity__sub">
            Select a legal entity above to view Department-wise Distribution,
            emission trends, department summaries, and analytics reports.
          </div>
        </div>
      )}

      {selectedEntity && (
        <>

      {/* DATE RANGE */}
      <div className="dash-card" style={{ marginTop: 20 }}>
        <div className="dash-date-top">
          <div className="dash-date-title">
            <CalendarIcon />
            Report Date Range
          </div>

          <div className="dash-range-pills">
            {ranges.map((r) => (
              <button
                key={r}
                className={`range-pill ${activeRange === r ? "active" : ""}`}
                onClick={() => {
                  setActiveRange(r);

                  const today = new Date();

                  let from = null;
                  let to = null;

                  if (r === "This Month") {
                    from = formatISO(
                      new Date(today.getFullYear(), today.getMonth(), 1)
                    );
                    to = formatISO(today);
                  }

                  if (r === "Last Month") {
                    const first = new Date(
                      today.getFullYear(),
                      today.getMonth() - 1,
                      1
                    );
                    const last = new Date(
                      today.getFullYear(),
                      today.getMonth(),
                      0
                    );
                    from = formatISO(first);
                    to = formatISO(last);
                  }

                  if (r === "This Year") {
                    const first = new Date(today.getFullYear(), 0, 1);
                    from = formatISO(first);
                    to = formatISO(today);
                  }

                  if (r === "This Quarter") {
                    const quarter = Math.floor(today.getMonth() / 3);
                    const first = new Date(today.getFullYear(), quarter * 3, 1);
                    from = formatISO(first);
                    to = formatISO(today);
                  }

                  setFromDate(from);
                  setToDate(to);

                  // ONLY filter, no date
                  setAppliedFilter({
                    filter: filterMap[r],
                    fromDate: null,
                    toDate: null,
                  });
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="dash-date-row">
          {/* FROM */}
          <div className="dash-date-field">
            <div className="dash-date-field-label">From Date</div>
            <div
              className="dash-date-input"
              style={{ position: "relative" }}
              onClick={() => openPicker(fromRef)}
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/747/747310.png"
                alt="calendar"
                className="dash-date-icon-img"
              />
              <span>{formatDisplay(fromDate)}</span>

              <input
                ref={fromRef}
                type="date"
                value={fromDate}
                min={minDate}
                max={maxDate}
                onChange={(e) => {
                  const value = e.target.value;

                  setFromDate(value);
                  setActiveRange(null);

                  const fy = getFYFromDate(value);

                  setReportingYear(fy);
                }}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>

          {/* TO */}
          <div className="dash-date-field">
            <div className="dash-date-field-label">To Date</div>
            <div
              className="dash-date-input"
              style={{ position: "relative" }}
              onClick={() => openPicker(toRef)}
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/747/747310.png"
                alt="calendar"
                className="dash-date-icon-img"
              />
              <span>{formatDisplay(toDate)}</span>
              <input
                ref={toRef}
                type="date"
                value={toDate}
                min={minDate}
                max={maxDate}
                onChange={(e) => {
                  const value = e.target.value;

                  setToDate(value);
                  setActiveRange(null);

                  const fy = getFYFromDate(value);

                  setReportingYear(fy);
                }}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>

          <div className="dash-date-applied">
            <div
              className="apply-btn-icon"
              onClick={() => {
                setAppliedFilter({
                  filter: null, // ❗ always null
                  fromDate,
                  toDate,
                });
              }}
              style={{ cursor: "pointer" }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
          </div>

          <div className="dash-divider"></div>

          {/* COMPARE */}
          <div className="dash-date-field">
            <div className="dash-date-field-label">
              Compare From <span className="optional">(optional)</span>
            </div>
            <div
              className="dash-date-input muted"
              style={{ position: "relative" }}
              onClick={() => openPicker(compareFromRef)}
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/747/747310.png"
                alt="calendar"
                className="dash-date-icon-img"
              />
              <span>
                {compareFrom ? formatDisplay(compareFrom) : "Select date"}
              </span>

              <input
                ref={compareFromRef}
                type="date"
                value={compareFrom}
                onChange={(e) => setCompareFrom(e.target.value)}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>

          <div className="dash-date-field">
            <div className="dash-date-field-label">
              Compare To <span className="optional">(optional)</span>
            </div>
            <div
              className="dash-date-input muted"
              style={{ position: "relative" }}
              onClick={() => openPicker(compareToRef)}
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/747/747310.png"
                alt="calendar"
                className="dash-date-icon-img"
              />
              <span>
                {compareTo ? formatDisplay(compareTo) : "Select date"}
              </span>

              <input
                ref={compareToRef}
                type="date"
                value={compareTo}
                onChange={(e) => setCompareTo(e.target.value)}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>
        </div>

        <div className="dash-date-applied-range">
          {formatDisplay(fromDate)} — {formatDisplay(toDate)}
        </div>
      </div>

      {/* CHARTS */}
      <div className="dash-card" style={{ marginTop: 20 }}>
        <div className="chart-row">
          <div className="chart-card" style={{ position: "relative" }}>
            <h4>Emissions Share by Department</h4>

            <ResponsiveContainer width="100%" height="100%">
              {tableData.length ? (
                <PieChart className="chartSize">
                  <Tooltip />

                  <Pie
                    data={pieData}
                    dataKey="value"
                    cx="45%"
                    cy="45%"
                    outerRadius={100}
                    innerRadius={48}
                    labelLine={true} // line enable
                    label={renderCustomLabel} // custom label
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              ) : (
                <NoData message="No Emissions Share data available" />
              )}
            </ResponsiveContainer>
          </div>

          <div className="chart-card" style={{ position: "relative" }}>
            <h4>Scope 1 vs Scope 2 by Department</h4>
            <ResponsiveContainer width="100%" height="100%">
              {tableData.length ? (
                <BarChart
                  className="chartSizedepartment"
                  data={barData}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" />
                  <Tooltip />
                  <Bar dataKey="scope1" stackId="a" fill="#2e944b" />
                  <Bar dataKey="scope2" stackId="a" fill="#f59e0b" />
                </BarChart>
              ) : (
                <NoData message="No Scope data available" />
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="dash-card" style={{ marginTop: 20 }}>
        <h5>Department Summary</h5>
        <br></br>

        <table className="dash-table">
          <thead>
            <tr>
              <th>DEPARTMENT</th>
              <th>SCOPE 1</th>
              <th>SCOPE 2</th>
              <th>TOTAL</th>
              <th>%</th>
            </tr>
          </thead>

          <tbody>
            {tableData.map((item, i) => (
              <tr key={i}>
                <td data-label="Location">{item.location}</td>
                <td data-label="Scope 1">{item.scope1}</td>
                <td data-label="Scope 2">{item.scope2}</td>
                <td data-label="Total">
                  <b>{item.total}</b>
                </td>
                <td data-label="% Share">{item.percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </>
      )}
    </div>
  );
};

export default ByDepartment;
